import React from "react";
import { NodeOrEdge } from "@hex-insights/app-modules";
import { Button, ClassNameProps, makeClassName, Row, StyleProps } from "@hex-insights/core";
import { Table } from "@hex-insights/tables";
import {
	asNode,
	BillingAdjustment,
	ContractInstallment,
	ContractNameFragment,
	Invoice,
	PaymentLineItem,
	Student,
	StudentNameAndImageFragment,
} from "../../../../Utilities";
import { ContractFormat, InvoiceFormat, ModelFormat, StudentFormat } from "../../../Format";
import { InvoiceUtilities } from "../../../Utilities";
import { paymentStatusColors, PaymentStatusIcon } from "../../_General";
import { StudentIcon } from "../../Student";
import styles from "./styles.module.css";

const columns = ["icon", "student", "contract", "description", "amountDue", "select"];
const columnWidths = {
	icon: "3rem",
	select: "4.5rem",
};

export type PaymentSelectionInvoice = Pick<Invoice, "id" | "dueDate"> & {
	contractInstallment: Pick<ContractInstallment, "id"> & {
		contract: ContractNameFragment & {
			student: Pick<Student, "id"> & StudentNameAndImageFragment;
			// Assumed to be in order
			contractInstallments: Pick<ContractInstallment, "id" | "dueDate">[];
		};
	};
	invoiceLineItems: Pick<PaymentLineItem, "amount">[];
	paymentLineItems: Pick<PaymentLineItem, "amount">[];
	billingAdjustments: Pick<BillingAdjustment, "amount">[];
};

export type InvoicePaymentSelectionProps = {
	isLoading?: boolean;
	invoices: NodeOrEdge<PaymentSelectionInvoice>[] | null | undefined;
	onClickInvoice: (invoice: PaymentSelectionInvoice & InvoiceUtilities.PrecomputedInvoice) => void;
	disabled?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function InvoicePaymentSelection({
	invoices,
	onClickInvoice,
	disabled,
	className,
	style,
}: InvoicePaymentSelectionProps) {
	const precomputedInvoices = React.useMemo(() => {
		if (!invoices) {
			return [];
		}

		const precomputedInvoices = [];
		for (let i = 0; i < invoices.length; i++) {
			const invoice = asNode(invoices[i]);
			const precomputedInvoice = InvoiceUtilities.precomputeInvoice(invoice);
			if (precomputedInvoice.amountDue <= 0) {
				continue;
			}
			const contractInstallment = invoice.contractInstallment;
			let contractInstallmentIndex = 0;
			for (let j = 0; j < invoice.contractInstallment.contract.contractInstallments.length; j++) {
				const otherContractInstallment = invoice.contractInstallment.contract.contractInstallments[j];
				if (otherContractInstallment.id === contractInstallment.id) {
					contractInstallmentIndex = j;
					break;
				}
			}
			const computedDescription = `Installment ${contractInstallmentIndex + 1} of ${
				invoice.contractInstallment.contract.contractInstallments.length
			}`;
			precomputedInvoices.push({ ...invoice, ...precomputedInvoice, computedDescription });
		}
		return precomputedInvoices;
	}, [invoices]);

	return (
		<Table
			columns={columns}
			columnWidths={columnWidths}
			className={makeClassName(styles["invoice-payment-selection"], className)}
			style={style}
		>
			<Table.Body>
				{precomputedInvoices.map((invoice) => {
					const contract = invoice.contractInstallment.contract;
					const student = contract.student;
					const colors = paymentStatusColors[invoice.status];

					return (
						<React.Fragment key={invoice.id}>
							<Table.Row className={styles["row--status"]} style={{ backgroundColor: colors.backgroundColor }}>
								<Table.Cell column={columns[0]} colspan={columns.length} oneLine>
									<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
										<Row
											justify="spaced-start"
											align="center"
											horizontalSpacing="0.25rem"
											style={{ color: colors.color }}
										>
											<PaymentStatusIcon status={invoice.status} size={16} color={colors.color} /> {invoice.status}
										</Row>
										<span style={{ color: "var(--neutral-colors--secondary)" }}>
											{InvoiceFormat.Fields.dueDate(invoice.dueDate)}
										</span>
									</Row>
								</Table.Cell>
							</Table.Row>
							<Table.Row style={{ "--table__cell---padding": "1.25rem 0.5rem" } as React.CSSProperties}>
								<Table.Cell column="icon" oneLine className={styles["cell--centered-content"]}>
									<Row justify="center" align="center" className={styles["cell--centered-content__content"]}>
										<StudentIcon student={student} size={32} />
									</Row>
								</Table.Cell>
								<Table.Cell column="student" oneLine>
									{StudentFormat.name(student)}
								</Table.Cell>
								<Table.Cell column="contract" oneLine>
									{ContractFormat.name(contract)}
								</Table.Cell>
								<Table.Cell column="description" oneLine>
									{invoice.computedDescription}
								</Table.Cell>
								<Table.Cell column="amountDue" oneLine numeric>
									{ModelFormat.money(invoice.amountDue)}
								</Table.Cell>
								<Table.Cell column="select" oneLine>
									<Button variant="link" size="small" onClick={() => onClickInvoice(invoice)} disabled={disabled}>
										Select
									</Button>
								</Table.Cell>
							</Table.Row>
						</React.Fragment>
					);
				})}
			</Table.Body>
		</Table>
	);
}
